import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiGet, apiPostPut, apiDelete, apiGetFactFinder } from "../api/api_methods";
import { rolesWithId } from "../constants/constants";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import { useStores } from "../stores";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

function Settings() {
  const { clientsStore } = useStores();

  // Memoize user to keep a stable reference for hooks dependencies
  const user = useMemo(() => {
    const storeUser = clientsStore.user;
    if (storeUser && Object.keys(storeUser).length > 0) {
      return storeUser;
    }
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return storedUser || {};
  }, [clientsStore.user]);

  const isAdmin = user.role === rolesWithId.admin;
  const isFinancialPlanner = user.role === rolesWithId.agent;

  // Set default tab based on user role (admins see planners first, others see clients)
  const [activeTab, setActiveTab] = useState(
    isAdmin ? "financial-planners" : "clients"
  );
  const [financialPlanners, setFinancialPlanners] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Pagination state for clients
  const [currentPage, setCurrentPage] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;
  
  // Fact Finder clients state
  // Fetch financial planners
  const fetchFinancialPlanners = async () => {
    try {
      setLoading(true);
      const response = await apiGet(
        "/users/get-users?role=6720e9990ca50db671add43e"
      );
      console.log("Financial planners response:", response.body);
      if (response.status === 200 && response.body.success) {
        // Extract from the correct nested structure
        const planners = response.body.data?.users || [];
        setFinancialPlanners(Array.isArray(planners) ? planners : []);
      } else {
        setFinancialPlanners([]);
      }
    } catch (error) {
      console.error("Error fetching financial planners:", error);
      setFinancialPlanners([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients with pagination (Awaken + Fact Finder combined view)
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      // Use the user from component state (already defined at top)
      // Convert 0-based currentPage to 1-based page number for API
      const pageNumber = currentPage + 1;
      const limit = itemsPerPage;
      
      // Fetch clients from all plan types
      const planTypes = ['comprehensive', 'term', 'health', 'pension', 'basic', 'zeroToOne', 'wealthManagement'];
      let allAwakenClients = [];
      let totalClientsCount = 0;

      // Fetch clients from each plan type
      for (const planType of planTypes) {
        try {
          let apiPath;
          if (isAdmin) {
            apiPath = `/adminPanel/get-clients?planType=${planType}&status=&page=1&limit=1000`;
          } else {
            apiPath = `/adminPanel/get-clients?planType=${planType}&status=&agentId=${user._id}&page=1&limit=1000`;
          }

          const response = await apiGet(apiPath);
          
          if (response.status === 200 && response.body.success) {
            const clients = response.body.data?.clients || response.body.data || [];
            allAwakenClients = allAwakenClients.concat(clients);
            
            // Get total count from pagination if available
            const pagination = response.body.data?.pagination || response.body.pagination || {};
            const planTypeCount = pagination.total || clients.length;
            totalClientsCount += planTypeCount;
          }
        } catch (error) {
          console.error(`Error fetching clients for planType ${planType}:`, error);
        }
      }

      console.log("All Awaken clients fetched:", allAwakenClients.length);
      console.log("Total clients count:", totalClientsCount);

      // Apply pagination to combined results
      const startIndex = (currentPage) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const awakenClients = allAwakenClients.slice(startIndex, endIndex);
      const awakenTotalCount = allAwakenClients.length;
      const awakenTotalPages = Math.ceil(awakenTotalCount / itemsPerPage);

      // --- Fetch Fact Finder clients (from external Fact Finder system) ---
      let factFinderClients = [];
      let factFinderTotalCount = 0;
      let factFinderTotalPages = 0;

      try {
        let ffApiPath = `/Exports/GetClients?page=${pageNumber}&pageSize=${limit}`;

        // For financial planners, mirror Fact Finder screen behaviour and filter by email
        const userEmail = user.email?.toLowerCase();
        if (isFinancialPlanner && userEmail) {
          const separator = ffApiPath.includes("?") ? "&" : "?";
          ffApiPath += `${separator}email=${encodeURIComponent(userEmail)}`;
        }

        console.log("Settings: fetching Fact Finder clients from:", ffApiPath);
        const ffResponse = await apiGetFactFinder(ffApiPath);

        if (ffResponse.status === 200 && ffResponse.body.success) {
          factFinderClients = ffResponse.body.data || [];
          factFinderTotalCount =
            ffResponse.body.totalRecords || factFinderClients.length;
          factFinderTotalPages =
            ffResponse.body.totalPages ||
            Math.ceil(factFinderTotalCount / limit);

          console.log("Settings: Fact Finder clients:", factFinderClients);
        } else {
          console.warn(
            "Settings: Fact Finder API not successful:",
            ffResponse
          );
        }
      } catch (ffError) {
        console.error("Settings: error fetching Fact Finder clients:", ffError);
      }

      // Normalise Awaken clients and Fact Finder clients into a single combined list
      const normalizedAwakenClients = (awakenClients || []).map((client) => ({
        ...client,
        source: "Awaken",
        // Extract factFinderId/uId for matching
        factFinderUId: client.factFinderId || client.factFinderUId || client.uId || client.uid,
      }));

      // Match Fact Finder clients with AWAKEN clients
      // Only include Fact Finder clients that have an AWAKEN form created
      const normalizedFactFinderClients = (factFinderClients || [])
        .map((client, index) => {
          const ffUId = client.uId || client.userId || client.id;
          
          // Check if this Fact Finder client has a matching AWAKEN form
          const matchingAwakenClient = normalizedAwakenClients.find(
            (awakenClient) => {
              const awakenUId = awakenClient.factFinderUId;
              return awakenUId && (awakenUId === ffUId || awakenUId === client.userId || awakenUId === client.id);
            }
          );

          if (matchingAwakenClient) {
            // This Fact Finder client has an AWAKEN form - use AWAKEN client data
            // Mark it as originally from Fact Finder but now manageable via AWAKEN
            return {
              ...matchingAwakenClient,
              source: "Fact Finder → Awaken", // Indicate it came from Fact Finder but is now in AWAKEN
              hasAwakenForm: true,
            };
          } else {
            // No AWAKEN form yet - return null to filter out
            return null;
          }
        })
        .filter(client => client !== null); // Remove Fact Finder clients without AWAKEN forms

      // Combine clients, but avoid duplicates (if Fact Finder client has AWAKEN form, 
      // it's already included via normalizedFactFinderClients, so don't add the AWAKEN version again)
      const awakenClientsWithoutMatches = normalizedAwakenClients.filter(
        (awakenClient) => {
          // Only include AWAKEN clients that don't have a matching Fact Finder entry
          return !normalizedFactFinderClients.some(
            (ffClient) => ffClient.hasAwakenForm && ffClient._id === awakenClient._id
          );
        }
      );

      const combinedClients = [
        ...awakenClientsWithoutMatches,
        ...normalizedFactFinderClients,
      ];

      const combinedTotalCount = awakenTotalCount + factFinderTotalCount;
      const combinedTotalPages = Math.max(
        awakenTotalPages || 0,
        factFinderTotalPages || 0
      );

      setClients(combinedClients);
      setClientsCount(combinedTotalCount);
      setTotalPages(combinedTotalPages || 1);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
      setClientsCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, isAdmin, isFinancialPlanner, user._id, user.email]);

  // Redirect financial planners away from admin-only tabs
  useEffect(() => {
    if (isFinancialPlanner && activeTab === "financial-planners") {
      setActiveTab("clients");
    }
  }, [isFinancialPlanner, activeTab]);

  useEffect(() => {
    // Only fetch financial planners if user is admin
    if (isAdmin) fetchFinancialPlanners();
    if (activeTab === "clients") fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch clients when page changes
  useEffect(() => {
    if (activeTab === "clients") {
      fetchClients();
    }
  }, [currentPage, activeTab, fetchClients]);

  // Reset to first page when switching tabs
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobileNumber || user.mobile || "",
      password: "", // Initialize empty for security
      // Add other fields as needed
    });
    setShowPassword(false); // Reset password visibility
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));

      let requestBody = {
        ...editFormData,
        userId: user._id,
      };

      // Only include password if it was changed (not empty)
      if (!editFormData.password) {
        delete requestBody.password;
      }

      let endpoint;
      if (activeTab === "financial-planners") {
        endpoint = `/users/${editingUser._id}`;
      } else {
        endpoint = `/adminPanel/clients/${editingUser._id}`;
      }

      const response = await apiPostPut(requestBody, endpoint, "PUT");

      if (response.status === 200 && response.body.success) {
        // Refresh the data
        if (activeTab === "financial-planners") {
          fetchFinancialPlanners();
        } else {
          fetchClients();
        }
        setEditingUser(null);
        setEditFormData({});
        setShowPassword(false);
      } else {
        alert(response.body.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error updating user");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      return;
    }

    try {
      setLoading(true);

      let endpoint;
      if (activeTab === "financial-planners") {
        endpoint = `/users/${user._id}`;
      } else {
        endpoint = `/adminPanel/clients/${user._id}`;
      }

      const response = await apiDelete(endpoint);

      if (response.status === 200 && response.body.success) {
        // Refresh the data
        if (activeTab === "financial-planners") {
          fetchFinancialPlanners();
        } else {
          fetchClients();
        }
      } else {
        alert(response.body.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Error deleting user");
    } finally {
      setLoading(false);
    }
  };

  const renderFinancialPlannersTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Mobile
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Password
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created On
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Source
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {financialPlanners.map((planner) => (
            <tr key={planner._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {editingUser && editingUser._id === planner._id ? (
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                    className="border px-2 py-1 rounded w-full"
                  />
                ) : (
                  planner.name
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {editingUser && editingUser._id === planner._id ? (
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        email: e.target.value,
                      })
                    }
                    className="border px-2 py-1 rounded w-full"
                  />
                ) : (
                  planner.email
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {editingUser && editingUser._id === planner._id ? (
                  <input
                    type="tel"
                    value={editFormData.mobile}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        mobile: e.target.value,
                      })
                    }
                    className="border px-2 py-1 rounded w-full"
                  />
                ) : (
                  planner.mobileNumber || planner.mobile || "N/A"
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {editingUser && editingUser._id === planner._id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={editFormData.password}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          password: e.target.value,
                        })
                      }
                      placeholder="New password (leave empty to keep current)"
                      className="border px-2 py-1 rounded w-full pr-8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <FaEyeSlash size={16} />
                      ) : (
                        <FaEye size={16} />
                      )}
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400 italic">••••••••</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatDate(planner.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                {editingUser && editingUser._id === planner._id ? (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="text-green-600 hover:text-green-900 px-2 py-1 rounded"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingUser(null);
                        setEditFormData({});
                        setShowPassword(false);
                      }}
                      className="text-gray-600 hover:text-gray-900 px-2 py-1 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(planner)}
                      className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(planner)}
                      className="text-red-600 hover:text-red-900 px-2 py-1 rounded"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };


  const renderClientsTable = () => {
    // Debug logging
    console.log("Pagination Debug:", {
      clientsCount,
      itemsPerPage,
      totalPages,
      currentPage,
      clientsLength: clients.length
    });
    
    return (
      <div>
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Mobile
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Plan Type
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Aw/FF
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created On
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    No clients found
                  </td>
                </tr>
              ) : (
                clients.map((client) => {
            // Debug logging for first few clients
            if (clients.indexOf(client) < 3) {
              console.log(`Client ${clients.indexOf(client) + 1}:`, client);
              console.log(`Client ${clients.indexOf(client) + 1} PAN fields:`, {
                pan: client.pan,
                panNumber: client.panNumber,
                hasOwnProperty_pan: client.hasOwnProperty("pan"),
                hasOwnProperty_panNumber: client.hasOwnProperty("panNumber"),
              });
            }

            return (
              <tr key={client._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {editingUser && editingUser._id === client._id ? (
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          name: e.target.value,
                        })
                      }
                      className="border px-2 py-1 rounded w-full"
                    />
                  ) : (
                    client.awareness?.personalDetails?.name || 
                    client.personalDetails?.name || 
                    client.name || 
                    client.fullName ||
                    "N/A"
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.awareness?.personalDetails?.email || 
                   client.personalDetails?.email || 
                   client.email || 
                   "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.awareness?.personalDetails?.phone || 
                   client.awareness?.personalDetails?.mobile || 
                   client.awareness?.personalDetails?.mobileNumber ||
                   client.personalDetails?.phone || 
                   client.personalDetails?.mobile || 
                   client.personalDetails?.mobileNumber ||
                   client.phone || 
                   client.mobile || 
                   client.mobileNumber ||
                   "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {clientsStore.camelToTitleCase(
                    client.awareness?.planDetails?.planType || 
                    client.planDetails?.planType || 
                    client.planType || 
                    "N/A"
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  {client.factFinderId || client.factFinderUId || client.uId || client.uid ? (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      Fact Finder
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                      Awaken
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(client.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingUser && editingUser._id === client._id ? (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleSaveEdit}
                        className="text-green-600 hover:text-green-900 px-2 py-1 rounded"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingUser(null)}
                        className="text-gray-600 hover:text-gray-900 px-2 py-1 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      {/* Show Edit/Delete for AWAKEN clients and Fact Finder clients that have AWAKEN forms */}
                      {(client.source === "Awaken" || client.source === "Fact Finder → Awaken" || client.hasAwakenForm) ? (
                        <>
                          <button
                            onClick={() => handleEdit(client)}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(client)}
                            className="text-red-600 hover:text-red-900 px-2 py-1 rounded"
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-500 italic">
                          Create AWAKEN form first
                        </span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
                })
              )}
        </tbody>
      </table>
        </div>
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <ReactPaginate
              nextLabel=">"
              previousLabel="<"
              onPageChange={handlePageChange}
              forcePage={currentPage}
              pageRangeDisplayed={2}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link px-3 py-2"
              containerClassName="flex items-center gap-1"
              pageClassName="page-item"
              pageLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              previousClassName="page-item"
              previousLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              nextClassName="page-item"
              nextLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              activeClassName="active"
              activeLinkClassName="bg-blue-500 text-white hover:bg-blue-600 font-semibold"
              disabledClassName="opacity-50 cursor-not-allowed"
              disabledLinkClassName="cursor-not-allowed"
              renderOnZeroPageCount={null}
            />
          </div>
        )}
    </div>
  );
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Settings</h2>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {/* Financial Planners tab - Admin only */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab("financial-planners")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "financial-planners"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Financial Planners
            </button>
          )}
          {/* Clients tab - Admin only */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab("clients")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "clients"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Clients
            </button>
          )}
        </nav>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div>
          {activeTab === "financial-planners" ? (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                Financial Planners
              </h3>
              {financialPlanners.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No financial planners found.
                </div>
              ) : (
                renderFinancialPlannersTable()
              )}
            </div>
          ) : activeTab === "clients" ? (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                Clients
              </h3>
              {clients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No clients found.
                </div>
              ) : (
                renderClientsTable()
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default Settings;
